import React, { useState, useEffect, useMemo } from "react";
import { 
    Card, Table, Row, Col, Form, InputGroup, Button, Badge, 
    Spinner, Alert, Tooltip, OverlayTrigger, Modal 
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatDate } from "../utils/formatters";
import './Factures.css';

export default function DevisAcceptes() {
    const navigate = useNavigate();
    const [devis, setDevis] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [stats, setStats] = useState(null);
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [showErrorAlert, setShowErrorAlert] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'date_proforma', direction: 'desc' });
    
    // États pour le modal d'envoi d'email
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [selectedDevis, setSelectedDevis] = useState(null);
    const [sendingEmail, setSendingEmail] = useState(false);
    const [emailForm, setEmailForm] = useState({
        destinataire: '',
        cc: '',
        bcc: '',
        sujet: '',
        message: '',
        inclurePdf: true
    });

    useEffect(() => {
        fetchDevisAcceptes();
        fetchStats();
    }, []);

    const fetchDevisAcceptes = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/devis.php?statut=accepte',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setDevis(result.data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/devis.php?action=stats',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur stats", err);
        }
    };

    const showSuccess = (message) => {
        setSuccessMessage(message);
        setShowSuccessAlert(true);
        setTimeout(() => setShowSuccessAlert(false), 3000);
    };

    const showError = (message) => {
        setErrorMessage(message);
        setShowErrorAlert(true);
        setTimeout(() => setShowErrorAlert(false), 3000);
    };

    const handleConvertirEnFacture = async (idDevis) => {
        try {
            const token = localStorage.getItem("token");
            const userStr = localStorage.getItem("user");
            const user = userStr ? JSON.parse(userStr) : null;

            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/devis.php',
                {
                    method: 'POST',
                    headers: { 
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        action: 'convertir',
                        id_devis: idDevis,
                        id_utilisateur: user?.id
                    })
                }
            );

            const result = await response.json();

            if (result.success) {
                showSuccess("Devis converti en facture avec succès");
                fetchDevisAcceptes();
                fetchStats();
            } else {
                showError(result.message || "Erreur lors de la conversion");
            }
        } catch (err) {
            showError("Erreur de connexion");
        }
    };

    // Fonction pour ouvrir le modal d'envoi d'email
    const handleOpenEmailModal = (devisItem) => {
        setSelectedDevis(devisItem);
        setEmailForm({
            destinataire: devisItem.client_email || '',
            cc: '',
            bcc: '',
            sujet: `Devis ${devisItem.numero_proforma} - ${devisItem.client_nom}`,
            message: generateDefaultMessage(devisItem),
            inclurePdf: true
        });
        setShowEmailModal(true);
    };

    // Générer le message par défaut
    const generateDefaultMessage = (devis) => {
        const validite = formatDate(devis.date_validite);
        
        return `Bonjour,

Nous avons le plaisir de vous adresser notre devis n° ${devis.numero_proforma} pour les prestations demandées.

Ce devis a été accepté et nous vous remercions pour votre confiance.

Vous trouverez en pièce jointe le détail de notre proposition.

Restant à votre disposition pour tout renseignement complémentaire, nous vous prions d'agréer, Madame, Monsieur, l'expression de nos salutations distinguées.

Cordialement,
L'équipe GESTION STOCK`;
    };

    // Fonction pour envoyer l'email
    const handleSendEmail = async (e) => {
        e.preventDefault();
        
        if (!emailForm.destinataire) {
            showError("Veuillez saisir une adresse email");
            return;
        }

        setSendingEmail(true);

        try {
            const token = localStorage.getItem("token");
            
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/send-email.php',
                {
                    method: 'POST',
                    headers: { 
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        to: emailForm.destinataire,
                        cc: emailForm.cc,
                        bcc: emailForm.bcc,
                        subject: emailForm.sujet,
                        message: emailForm.message,
                        devis_id: selectedDevis.id_proforma,
                        include_pdf: emailForm.inclurePdf
                    })
                }
            );

            const result = await response.json();

            if (result.success) {
                setShowEmailModal(false);
                showSuccess("Email envoyé avec succès !");
                fetchDevisAcceptes(); // Rafraîchir pour mettre à jour le statut
            } else {
                showError(result.message || "Erreur lors de l'envoi de l'email");
            }
        } catch (err) {
            console.error("Erreur envoi email:", err);
            showError("Erreur de connexion lors de l'envoi de l'email");
        } finally {
            setSendingEmail(false);
        }
    };

    const filteredDevis = useMemo(() => {
        return devis
            .filter(d => 
                d.numero_proforma?.toLowerCase().includes(filterText.toLowerCase()) ||
                d.client_nom?.toLowerCase().includes(filterText.toLowerCase()) ||
                d.code_client?.toLowerCase().includes(filterText.toLowerCase())
            )
            .sort((a, b) => {
                if (sortConfig.key === 'date_proforma') {
                    return sortConfig.direction === 'asc' 
                        ? new Date(a.date_proforma) - new Date(b.date_proforma)
                        : new Date(b.date_proforma) - new Date(a.date_proforma);
                }
                if (sortConfig.key === 'montant') {
                    return sortConfig.direction === 'asc'
                        ? a.net_a_payer - b.net_a_payer
                        : b.net_a_payer - a.net_a_payer;
                }
                return 0;
            });
    }, [devis, filterText, sortConfig]);

    // Fonction utilitaire pour nettoyer les montants
    const nettoyerMontant = (valeur) => {
        if (typeof valeur === 'number') return valeur;
        return parseFloat(String(valeur).replace(/[^\d.-]/g, '')) || 0;
    };

    // Calcul du total
    const totalAcceptes = filteredDevis.reduce((acc, d) => acc + nettoyerMontant(d.net_a_payer), 0);

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const SortableHeader = ({ field, children }) => (
        <th 
            onClick={() => handleSort(field)}
            style={{ cursor: 'pointer', userSelect: 'none' }}
            className="sortable-header"
        >
            <div className="d-flex align-items-center gap-1">
                {children}
                {sortConfig.key === field && (
                    <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line`}></i>
                )}
            </div>
        </th>
    );

    const ActionButton = ({ variant, icon, onClick, tooltip, disabled = false }) => (
        <OverlayTrigger
            placement="top"
            overlay={<Tooltip id={`tooltip-${variant}`}>{tooltip}</Tooltip>}
        >
            <Button
                variant={variant}
                size="sm"
                onClick={onClick}
                disabled={disabled}
                className="action-btn"
            >
                <i className={icon}></i>
            </Button>
        </OverlayTrigger>
    );

    if (loading) {
        return (
            <React.Fragment>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                        <div className="loading-container">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3 text-muted">Chargement des devis acceptés...</p>
                        </div>
                    </div>
                </div>
            </React.Fragment>
        );
    }

    if (error) {
        return (
            <React.Fragment>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="text-center py-5">
                        <i className="ri-error-warning-line fs-48 mb-3 d-block"></i>
                        <h4>{error}</h4>
                        <Button 
                            variant="outline-danger" 
                            className="mt-3"
                            onClick={fetchDevisAcceptes}
                        >
                            <i className="ri-refresh-line me-2"></i>
                            Réessayer
                        </Button>
                    </Alert>
                </div>
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid">
                    {/* Alertes */}
                    {showSuccessAlert && (
                        <Alert variant="success" onClose={() => setShowSuccessAlert(false)} dismissible className="mb-4 alert-modern">
                            <i className="ri-checkbox-circle-line me-2 fs-5"></i>
                            {successMessage}
                        </Alert>
                    )}
                    
                    {showErrorAlert && (
                        <Alert variant="danger" onClose={() => setShowErrorAlert(false)} dismissible className="mb-4 alert-modern">
                            <i className="ri-error-warning-line me-2 fs-5"></i>
                            {errorMessage}
                        </Alert>
                    )}

                    {/* En-tête */}
                    <Row className="mb-4 fade-in">
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center header-section">
                                <div className="d-flex align-items-center gap-3">
                                    <div className="page-icon bg-success-soft">
                                        <i className="ri-check-double-line text-success"></i>
                                    </div>
                                    <div>
                                        <h1 className="page-title mb-1">Devis acceptés</h1>
                                        <p className="page-subtitle mb-0">
                                            Gérez les devis acceptés et convertissez-les en factures
                                        </p>
                                    </div>
                                </div>
                                <Button 
                                    variant="primary" 
                                    onClick={() => navigate('/ventes/devis')}
                                    className="btn-gradient px-4 py-2"
                                >
                                    <i className="ri-arrow-left-line me-2"></i>
                                    Tous les devis
                                </Button>
                            </div>
                        </Col>
                    </Row>

                    {/* Statistiques */}
                    <Row className="g-3 mb-4 stats-row">
                        {/* Carte 1 - Devis acceptés */}
                        <Col sm={6} lg={4}>
                            <Card className="stat-card border-0 shadow-sm h-100">
                                <Card.Body className="p-3">
                                    <div className="d-flex align-items-center">
                                        <div 
                                            className="stat-icon-wrapper d-flex align-items-center justify-content-center me-3" 
                                            style={{ 
                                                width: '60px', 
                                                height: '60px', 
                                                borderRadius: '16px',
                                                background: 'linear-gradient(135deg, #10b981, #059669)',
                                                boxShadow: '0 10px 20px rgba(16, 185, 129, 0.25)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0
                                            }}
                                        >
                                            <i className="ri-check-double-line text-white" style={{ fontSize: '30px', lineHeight: 1 }}></i>
                                        </div>
                                        <div className="stat-content" style={{ flex: 1, minWidth: 0 }}>
                                            <span className="stat-label d-block text-uppercase small fw-semibold text-secondary mb-1">
                                                Devis acceptés
                                            </span>
                                            <h4 className="stat-value text-success mb-0 fw-bold" style={{ fontSize: '2rem', lineHeight: 1.2 }}>
                                                {filteredDevis.length}
                                            </h4>
                                            <small className="stat-trend text-success d-flex align-items-center gap-1 mt-1">
                                                <i className="ri-file-copy-line"></i>
                                                <span className="text-truncate">En attente de conversion</span>
                                            </small>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        {/* Carte 2 - Montant total */}
                        <Col sm={6} lg={4}>
                            <Card className="stat-card border-0 shadow-sm h-100">
                                <Card.Body className="p-3">
                                    <div className="d-flex align-items-center">
                                        <div 
                                            className="stat-icon-wrapper d-flex align-items-center justify-content-center me-3" 
                                            style={{ 
                                                width: '60px', 
                                                height: '60px', 
                                                borderRadius: '16px',
                                                background: 'linear-gradient(135deg, #4361ee, #3a56d4)',
                                                boxShadow: '0 10px 20px rgba(67, 97, 238, 0.25)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0
                                            }}
                                        >
                                            <i className="ri-money-euro-circle-line text-white" style={{ fontSize: '30px', lineHeight: 1 }}></i>
                                        </div>
                                        <div className="stat-content" style={{ flex: 1, minWidth: 0 }}>
                                            <span className="stat-label d-block text-uppercase small fw-semibold text-secondary mb-1">
                                                Montant total
                                            </span>
                                            <h4 className="stat-value text-primary mb-0 fw-bold" style={{ fontSize: '1.8rem', lineHeight: 1.2 }}>
                                                {formatCurrency(totalAcceptes)}
                                            </h4>
                                            <small className="stat-trend text-primary d-flex align-items-center gap-1 mt-1">
                                                <i className="ri-arrow-up-line"></i>
                                                <span className="text-truncate">CA potentiel</span>
                                            </small>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        {/* Carte 3 - Moyenne */}
                        <Col sm={6} lg={4}>
                            <Card className="stat-card border-0 shadow-sm h-100">
                                <Card.Body className="p-3">
                                    <div className="d-flex align-items-center">
                                        <div 
                                            className="stat-icon-wrapper d-flex align-items-center justify-content-center me-3" 
                                            style={{ 
                                                width: '60px', 
                                                height: '60px', 
                                                borderRadius: '16px',
                                                background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                                                boxShadow: '0 10px 20px rgba(59, 130, 246, 0.25)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0
                                            }}
                                        >
                                            <i className="ri-bar-chart-2-line text-white" style={{ fontSize: '30px', lineHeight: 1 }}></i>
                                        </div>
                                        <div className="stat-content" style={{ flex: 1, minWidth: 0 }}>
                                            <span className="stat-label d-block text-uppercase small fw-semibold text-secondary mb-1">
                                                Moyenne
                                            </span>
                                            <h4 className="stat-value text-info mb-0 fw-bold" style={{ fontSize: '1.8rem', lineHeight: 1.2 }}>
                                                {filteredDevis.length > 0 
                                                    ? formatCurrency(totalAcceptes / filteredDevis.length)
                                                    : formatCurrency(0)}
                                            </h4>
                                            <small className="stat-trend text-info d-flex align-items-center gap-1 mt-1">
                                                <i className="ri-bar-chart-2-line"></i>
                                                <span className="text-truncate">Par devis</span>
                                            </small>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Filtres */}
                    <Card className="filter-card border-0 mb-4">
                        <Card.Body>
                            <Row className="g-3">
                                <Col md={8}>
                                    <InputGroup className="search-input">
                                        <InputGroup.Text>
                                            <i className="ri-search-line"></i>
                                        </InputGroup.Text>
                                        <Form.Control
                                            placeholder="Rechercher devis, client..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                        />
                                        {filterText && (
                                            <Button 
                                                variant="outline-secondary" 
                                                onClick={() => setFilterText('')}
                                                className="border-0"
                                            >
                                                <i className="ri-close-line"></i>
                                            </Button>
                                        )}
                                    </InputGroup>
                                </Col>
                                <Col md={4} className="d-flex justify-content-end align-items-center">
                                    <div className="filter-info">
                                        <span className="badge-count">{filteredDevis.length}</span>
                                        <span className="text-muted ms-2">devis accepté(s)</span>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Liste des devis acceptés */}
                    <Card className="table-card border-0">
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="mb-0 align-middle custom-table">
                                    <thead>
                                        <tr>
                                            <th>N° Devis</th>
                                            <SortableHeader field="date_proforma">Date</SortableHeader>
                                            <th>Client</th>
                                            <th>Validité</th>
                                            <SortableHeader field="montant">Montant TTC</SortableHeader>
                                            <th className="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredDevis.length > 0 ? (
                                            filteredDevis.map((devisItem) => (
                                                <tr key={devisItem.id_proforma} className="facture-row">
                                                    <td>
                                                        <Link 
                                                            to={`/ventes/DetailDevis/${devisItem.id_proforma}`} 
                                                            className="facture-link"
                                                            title={devisItem.numero_proforma}
                                                        >
                                                            <span className="facture-num">
                                                                <i className="ri-file-copy-line text-success me-1"></i>
                                                                {devisItem.numero_proforma?.length > 8 
                                                                    ? devisItem.numero_proforma.substring(0, 8) + '...' 
                                                                    : devisItem.numero_proforma}
                                                            </span>
                                                        </Link>
                                                    </td>
                                                    <td>
                                                        <span className="facture-date">
                                                            {formatDate(devisItem.date_proforma)}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className="client-info">
                                                            <span className="client-name fw-bold">{devisItem.client_nom}</span>
                                                            <p style={{fontSize:'10px'}} className="client-code">{devisItem.code_client}</p>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className="echeance-date">
                                                            {formatDate(devisItem.date_validite)}
                                                        </span>
                                                    </td>
                                                    <td style={{minWidth:'150px'}} className="text-end">
                                                        <span className="montant-ttc fw-bold text-primary">
                                                            {formatCurrency(devisItem.net_a_payer)}
                                                        </span>
                                                    </td>
                                                    <td style={{minWidth:'180px'}} className="text-center">
                                                        <div className="actions-container">
                                                            <ActionButton
                                                                variant="outline-info"
                                                                icon="ri-eye-line"
                                                                onClick={() => navigate(`/ventes/DetailDevis/${devisItem.id_proforma}`)}
                                                                tooltip="Voir détails"
                                                            />
                                                            <ActionButton
                                                                variant="outline-success"
                                                                icon="ri-file-list-3-line"
                                                                onClick={() => handleConvertirEnFacture(devisItem.id_proforma)}
                                                                tooltip="Convertir en facture"
                                                            />
                                                            <ActionButton
                                                                variant="outline-warning"
                                                                icon="ri-mail-send-line"
                                                                onClick={() => handleOpenEmailModal(devisItem)}
                                                                tooltip="Envoyer par email"
                                                            />
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="6" className="text-center py-5">
                                                    <div className="empty-state">
                                                        <i className="ri-check-double-line empty-icon text-success"></i>
                                                        <h5 className="mt-3">Aucun devis accepté</h5>
                                                        <p className="text-muted mb-3">
                                                            {filterText 
                                                                ? "Aucun résultat ne correspond à votre recherche"
                                                                : "Les devis acceptés apparaîtront ici"}
                                                        </p>
                                                        {filterText && (
                                                            <Button 
                                                                variant="outline-primary"
                                                                onClick={() => setFilterText('')}
                                                            >
                                                                <i className="ri-refresh-line me-2"></i>
                                                                Réinitialiser
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>
                </div>
                <Footer />
            </div>

            {/* Modal d'envoi d'email */}
            <Modal show={showEmailModal} onHide={() => setShowEmailModal(false)} size="lg" centered className="modern-modal">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="ri-mail-send-line me-2 text-warning"></i>
                        Envoyer le devis par email
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSendEmail}>
                    <Modal.Body>
                        {selectedDevis && (
                            <>
                                <Alert style={{position:'relative'}} variant="info" className="mb-3 alert-modern">
    <div className="d-flex justify-content-between align-items-center">
        <div>
            <h6 className="mb-1 text-primary">Devis {selectedDevis.numero_proforma}</h6>
            <p className="mb-0 text-muted small">
                <i className="ri-user-line me-1"></i>
                {selectedDevis.client_nom}
                {selectedDevis.code_client && (
                    <span className="ms-2">
                        <i className="ri-barcode-line me-1"></i>
                        {selectedDevis.code_client}
                    </span>
                )}
            </p>
        </div>
        <Badge 
        
            bg="success" 
            className="px-3 py-2 d-flex align-items-center gap-2"
            style={{ 
                background: 'linear-gradient(135deg, #10b981, #059669)',
                border: 'none',
                borderRadius: '30px',
               
                whiteSpace: 'nowrap',
                position:'absolute',
                right:'5px'
            }}
        >
            <i className="ri-check-double-line"></i>
            <span>Accepté</span>
        </Badge>
    </div>
</Alert>

                                <Row className="g-3">
                                    <Col md={12}>
                                        <Form.Group>
                                            <Form.Label>Destinataire <span className="text-danger">*</span></Form.Label>
                                            <InputGroup>
                                                <InputGroup.Text>
                                                    <i className="ri-mail-line"></i>
                                                </InputGroup.Text>
                                                <Form.Control
                                                    type="email"
                                                    value={emailForm.destinataire}
                                                    onChange={(e) => setEmailForm({...emailForm, destinataire: e.target.value})}
                                                    placeholder="client@email.com"
                                                    required
                                                />
                                            </InputGroup>
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>CC</Form.Label>
                                            <InputGroup>
                                                <InputGroup.Text>
                                                    <i className="ri-mail-line"></i>
                                                </InputGroup.Text>
                                                <Form.Control
                                                    type="email"
                                                    value={emailForm.cc}
                                                    onChange={(e) => setEmailForm({...emailForm, cc: e.target.value})}
                                                    placeholder="cc@email.com"
                                                />
                                            </InputGroup>
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>BCC</Form.Label>
                                            <InputGroup>
                                                <InputGroup.Text>
                                                    <i className="ri-mail-line"></i>
                                                </InputGroup.Text>
                                                <Form.Control
                                                    type="email"
                                                    value={emailForm.bcc}
                                                    onChange={(e) => setEmailForm({...emailForm, bcc: e.target.value})}
                                                    placeholder="bcc@email.com"
                                                />
                                            </InputGroup>
                                        </Form.Group>
                                    </Col>

                                    <Col md={12}>
                                        <Form.Group>
                                            <Form.Label>Sujet</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={emailForm.sujet}
                                                onChange={(e) => setEmailForm({...emailForm, sujet: e.target.value})}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={12}>
                                        <Form.Group>
                                            <Form.Label>Message</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={6}
                                                value={emailForm.message}
                                                onChange={(e) => setEmailForm({...emailForm, message: e.target.value})}
                                                style={{ fontFamily: 'Arial, sans-serif' }}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={12}>
                                        <Form.Group>
                                            <Form.Check
                                                type="checkbox"
                                                id="include-pdf"
                                                label="Joindre le devis au format PDF"
                                                checked={emailForm.inclurePdf}
                                                onChange={(e) => setEmailForm({...emailForm, inclurePdf: e.target.checked})}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowEmailModal(false)}>
                            Annuler
                        </Button>
                        <Button variant="warning" type="submit" disabled={sendingEmail}>
                            {sendingEmail ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Envoi en cours...
                                </>
                            ) : (
                                <>
                                    <i className="ri-send-plane-line me-2"></i>
                                    Envoyer
                                </>
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </React.Fragment>
    );
}